import { useMemo } from 'react';

interface KycStatusTimelineProps {
  status: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

interface Step {
  key: string;
  label: string;
  icon: string;
  date: string | null;
}

export default function KycStatusTimeline({ status, submittedAt, reviewedAt, createdAt }: KycStatusTimelineProps) {
  const steps: Step[] = useMemo(() => [
    { key: 'draft', label: 'Draft', icon: 'ri-draft-line', date: createdAt },
    { key: 'submitted', label: 'Submitted', icon: 'ri-send-plane-line', date: submittedAt },
    { key: 'review', label: 'Under Review', icon: 'ri-eye-line', date: status === 'under review' ? reviewedAt || submittedAt : reviewedAt },
    { key: 'completed', label: status === 'rejected' ? 'Rejected' : 'Approved', icon: status === 'rejected' ? 'ri-close-circle-line' : 'ri-check-double-line', date: reviewedAt },
  ], [status, submittedAt, reviewedAt, createdAt]);

  const statusOrder = ['draft', 'submitted', 'under review', 'approved', 'rejected'];
  const currentIndex = statusOrder.indexOf(status);

  return (
    <div className="bg-white rounded-xl border border-stone-100 p-5 mb-6">
      <h3 className="font-bold text-stone-900 text-sm mb-4">KYC Request Status</h3>
      <div className="flex items-start justify-between gap-2 relative">
        {/* Connector line */}
        <div className="absolute top-5 left-0 right-0 mx-8 h-0.5 bg-stone-100 hidden sm:block" />
        <div className="absolute top-5 left-0 right-0 mx-8 h-0.5 hidden sm:block"
          style={{
            width: currentIndex >= 0 ? `${(currentIndex / (steps.length - 1)) * 100}%` : '0%',
            background: status === 'rejected'
              ? '#ef4444'
              : status === 'approved'
              ? '#10b981'
              : '#f97316',
          }}
        />

        {steps.map((step, idx) => {
          const stepStatusOrder = ['draft', 'submitted', 'under review', 'approved'];
          const stepIdx = stepStatusOrder.indexOf(step.key === 'completed' ? (status === 'rejected' ? 'rejected' : 'approved') : step.key);
          const isCompleted = currentIndex >= stepIdx;
          const isCurrent = status === step.key || (step.key === 'completed' && (status === 'approved' || status === 'rejected'));

          const colorClass = status === 'rejected' && isCompleted
            ? 'bg-red-50 text-red-600 border-red-200'
            : status === 'approved' && isCompleted
            ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
            : isCompleted
            ? 'bg-brand-50 text-brand-600 border-brand-200'
            : 'bg-stone-50 text-stone-400 border-stone-200';

          return (
            <div key={step.key} className="flex flex-col items-center gap-2 z-10 relative flex-1 min-w-0">
              <div className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 transition-colors ${colorClass}`}>
                <i className={`${step.icon} text-sm w-4 h-4 flex items-center justify-center`} />
              </div>
              <div className="text-center min-w-0">
                <p className={`text-xs font-medium truncate ${isCurrent ? 'text-stone-900' : isCompleted ? 'text-stone-700' : 'text-stone-400'}`}>
                  {step.label}
                </p>
                {step.date && (
                  <p className="text-stone-400 text-[10px] mt-0.5">
                    {new Date(step.date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}